import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Info } from 'lucide-react';
import { getEventFacets, getEventGraph, getEventSuggestions, saveEventGraph, type EventFacet, type EventGraph, type EventAttributeSuggestion } from '@/api/event-graph';
import { ApiError } from '@/api/client';

const labels: Record<string, string> = {
  practice: 'Cultural practices',
  experience: 'What people can do',
  audience: 'Who is welcome',
  familiarity: 'Prior experience',
  food: 'Food available',
  participation: 'Ways to take part',
};

export function EventGraphPanel({ eventId }: { eventId: string }) {
  const catalog = useQuery({ queryKey: ['event-facets'], queryFn: getEventFacets });
  const graph = useQuery({ queryKey: ['event-graph', eventId], queryFn: () => getEventGraph(eventId) });
  const pending = useQuery({ queryKey: ['event-suggestions', eventId, graph.data?.data.revision], queryFn: () => getEventSuggestions(eventId), enabled: Boolean(graph.data) });
  if (catalog.isError || graph.isError || pending.isError) return (
    <div role="alert">
      <p>We could not load event attributes.</p>
      <button className="btn-secondary mt-2" onClick={() => { void catalog.refetch(); void graph.refetch(); void pending.refetch(); }}>Try again</button>
    </div>
  );
  if (catalog.isPending || graph.isPending || pending.isPending) return <p role="status">Loading event attributes…</p>;
  return <GraphEditor key={eventId + ':' + graph.data.data.revision} graph={graph.data.data} catalog={catalog.data.data.facets} suggestions={pending.data.data.suggestions} />;
}

function GraphEditor({ graph, catalog, suggestions }: { graph: EventGraph; catalog: EventFacet[]; suggestions: EventAttributeSuggestion[] }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(() => graph.facets.map((f) => f.code));
  const [message, setMessage] = useState('');
  const save = useMutation({
    mutationFn: () => saveEventGraph(graph.eventId, graph.revision, selected),
    onSuccess: (response) => {
      qc.setQueryData(['event-graph', graph.eventId], response);
    },
  });
  const options = [...catalog, ...graph.facets.filter((f) => !catalog.some((c) => c.code === f.code))];
  const groups = [...new Set(options.map((f) => f.dimension))];
  const dirty = selected.length !== graph.facets.length || selected.some((code) => !graph.facets.some((f) => f.code === code));
  const toggle = (code: string) => {
    setMessage('');
    setSelected((current) => current.includes(code) ? current.filter((c) => c !== code) : [...current, code]);
  };
  const canonical = graph.canonical;
  const stale = save.error instanceof ApiError && save.error.code === 'EVENT_GRAPH_CHANGED';

  return (
    <section aria-labelledby="event-attributes-title" className="space-y-4 border border-gray-200 rounded-xl p-4">
      <div>
        <h3 id="event-attributes-title" className="font-semibold text-gray-900">Help people know what to expect</h3>
        <p className="text-sm text-gray-600 mt-1">Select only facts you can confirm for this event. Members will see that the organizer confirmed them.</p>
      </div>
      <div className="text-sm text-gray-600 space-y-1">
        <p className="flex items-center gap-2 font-medium"><Info size={16} aria-hidden="true" />Already on this event</p>
        <p>Languages: {canonical.languages.join(' · ') || 'Not listed'}</p>
        <p>Regional context: {canonical.relatedStates.join(' · ') || 'Not listed'}</p>
        {canonical.foodInfo && <p>Food guidance: {canonical.foodInfo}</p>}
        {canonical.festivalCode && <p>Festival: {canonical.festivalCode}</p>}
        {canonical.rituals.length > 0 && <p>Rituals: {canonical.rituals.join(' · ')}</p>}
        <p className="text-xs">These facts come from the event details. Saving attributes keeps them intact.</p>
      </div>
      {options.length === 0 && <p role="status">No attributes are available yet. Ask the operator to load the event attribute catalog.</p>}
      {suggestions.length > 0 && <p className="text-sm text-amber-800" role="status">Suggestions from existing event text need your review. They are not public or organizer-confirmed. Check the source, especially if the event contains demo content.</p>}
      {groups.map((dimension) => (
        <fieldset key={dimension} disabled={save.isPending}>
          <legend className="text-sm font-semibold mb-2">{labels[dimension] ?? dimension}</legend>
          <div className="grid sm:grid-cols-2 gap-2">
            {options.filter((f) => f.dimension === dimension).map((facet) => (
              <label key={facet.code} className="flex items-start gap-2 p-2 rounded-lg border border-gray-200 cursor-pointer">
                <input type="checkbox" className="mt-1" disabled={suggestions.some((s) => s.code === facet.code && s.stale)} checked={selected.includes(facet.code)} onChange={() => toggle(facet.code)} />
                <span>
                  <span className="block text-sm font-medium">{facet.label}</span>
                  <span className="block text-xs text-gray-500">{facet.description}</span>
                  {suggestions.filter((s) => s.code === facet.code).map((suggestion) => <span key={suggestion.code} className="block text-xs text-amber-800 mt-1">
                    {suggestion.stale ? 'Source changed. Ask the operator to refresh this suggestion.' : 'Unconfirmed suggestion from event text.'}
                    {suggestion.evidenceText && <span className="block whitespace-pre-line">Source: “{suggestion.evidenceText}”</span>}
                  </span>)}
                  {!catalog.some((c) => c.code === facet.code) && <span className="block text-xs text-amber-700">Retired attribute. You can keep or remove it.</span>}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      {graph.facets.length > 0 && (
        <p className="flex items-center gap-2 text-sm text-green-700" role="status">
          <CheckCircle2 size={16} aria-hidden="true" />{graph.facets.length} organizer-confirmed attributes saved
        </p>
      )}
      {save.error && <p role="alert" className="text-sm text-red-700">{save.error.message}</p>}
      {stale && <button className="btn-secondary" onClick={() => void qc.invalidateQueries({ queryKey: ['event-graph', graph.eventId] })}>Reload saved attributes</button>}
      {message && <p role="status" className="text-sm">{message}</p>}
      <div className="flex flex-wrap gap-3 items-center">
        <button className="btn-primary" disabled={!dirty || save.isPending || options.length === 0} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Confirm and save attributes'}
        </button>
        <button className="btn-secondary" disabled={selected.length === 0 || save.isPending} onClick={() => { setSelected([]); setMessage('All selections cleared. Save to remove them from this event.'); }}>Clear selection</button>
      </div>
    </section>
  );
}
